import * as Yup from 'yup';

export const validationSchema = Yup.object().shape({
  fullName: Yup.string()
    .required('Cardholder name is required')
    .min(3, 'Cardholder name must be at least 3 characters long')
    .max(50, 'Cardholder name cannot exceed 50 characters')
    .matches(
      /^[A-Za-z\s.'-]+$/, // Allows letters, spaces, apostrophes, hyphens, and periods
      'Cardholder name can only contain letters, spaces, periods, apostrophes, and hyphens'
    )
    .test(
      'two-words-min',
      'Cardholder name should contain at least two words (e.g., First Last)',
      value => {
        if (!value) return true; // Allows required() to handle empty cases
        const words = value.trim().split(/\s+/);
        return words.length >= 2;
      }
    ),
  cardNumber: Yup.string()
    .required('Card number is required')
    .matches(/^[0-9 ]+$/, 'Card number can only contain digits and spaces')
    .transform(value => (value ?? '').replace(/\D/g, ''))
    .test('luhn', 'Invalid card number', value => {
      if (!value || isNaN(Number(value))) {
        return false;
      }

      let sum = 0;
      let isEven = false;

      for (let i = value.length - 1; i >= 0; i--) {
        let digit = Number(value[i]);
        if (isEven) {
          digit = digit * 2;
          if (digit > 9) digit -= 9;
        }
        sum += digit;
        isEven = !isEven;
      }

      return sum % 10 === 0;
    })
    .test('length', 'Card number must be between 12-19 digits', value =>
      value ? value.length >= 12 && value.length <= 19 : false
    ),

  cvv: Yup.string()
    .required('CVV is required')
    .matches(/^[0-9]{3,4}$/, 'CVV must be 3-4 digits'),

  expirationDate: Yup.string()
    .required('Expiration date is required')
    .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Must be in MM/YY format')
    .test('expiry', 'Card has expired', function (value) {
      if (!value) return false;

      const [month, year] = value.split('/');
      if (!month || !year) return false;

      const expMonth = parseInt(month, 10);
      const expYear = parseInt(year, 10);
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear() % 100;
      const currentMonth = currentDate.getMonth() + 1;

      if (expYear < currentYear) return false;
      if (expYear === currentYear && expMonth < currentMonth) return false;
      return true;
    })
    .test('valid-year', 'Invalid expiry year', value => {
      if (!value) return false;
      const [, year] = value.split('/');
      if (!year) return false;
      const currentYear = new Date().getFullYear() % 100;
      const yearNum = parseInt(year, 10);
      return yearNum >= currentYear && yearNum <= currentYear + 20;
    }),

  // validates both canadian postal codes and US zip codes
  postalCode: Yup.string()
    .required('Postal/ZIP code is required')
    .transform(v => (v ? v.toUpperCase().trim() : v))
    .test('zip-or-ca', 'Enter a valid Postal/Zip code', value => {
      if (!value) return false;
      const ZIP_OR_POSTAL =
        /^(?:\d{5}(?:-\d{4})?|[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d)$/i;
      return ZIP_OR_POSTAL.test(value);
    }),

  saveCard: Yup.boolean(),
});
